import CyrillicToTranslit from 'cyrillic-to-translit';
import { Typography } from '@mui/joy';

export function openSidebar() {
  if (typeof window !== 'undefined') {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.setProperty('--SideNavigation-slideIn', '1');
  }
}

export function closeSidebar() {
  if (typeof window !== 'undefined') {
    document.documentElement.style.removeProperty('--SideNavigation-slideIn');
    document.body.style.removeProperty('overflow');
  }
}

export function toggleSidebar() {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const slideIn = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--SideNavigation-slideIn');
    if (slideIn) {
      closeSidebar();
    } else {
      openSidebar();
    }
  }
}

export function snakeToCamelCase(str) {
  let parts = str.split('_')
  let first = parts[0]
  let rest = parts.slice(1).map(part => capitalizeFirstLetter(part))
  return [first, ...rest].join("")
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const cyrillicToTranslitTJ = new CyrillicToTranslit({ preset: "tj" });
const cyrillicToTranslitRU = new CyrillicToTranslit({ preset: "ru" });

export function toSlug(str) {
  let translit = cyrillicToTranslitTJ.transform(str, '-');
  translit = cyrillicToTranslitRU.transform(translit, '-');
  translit = translit.toLowerCase()
  translit = translit.replace(/[^a-z0-9\-]/g, '')
  translit = translit.replace(/\-+/g, '-'); // --- => -
  if (translit.endsWith('-')) translit = translit.slice(0, -1)
  return translit
}

export function updateErrors(field, error, setErrors) {
  setTimeout(() => setErrors(errors => ({ errors, [field]: error })), 10)
}


export function fillFullTranslations(id, slugs, langs_db, language, translations) {
  const full_translations = [];
  for (const lang of langs_db) {
    if (!id || language?.id == lang.id) continue;
    const tr = translations.filter(t => t.id == lang.id)[0];
    if (tr) full_translations.push({ lang_id: lang.id, link: `/${slugs[0]}/${tr.entity_id}`, id: tr.id });
    else full_translations.push({ lang_id: lang.id, link: `/${slugs[1]}/add/${lang.code}/${id}` });
  }
  return full_translations;
}

export function textOrNull(data) {
  if (!data || data === '') return null;
  data = String(data).trim();
  if (data === '') return null;
  return data;
}

export function dateOrNull(data) {
  if (!data || data === '') return null;
  data = String(data).trim();
  if (data === '') return null;
  if (data.match(/^\d{4}-\d{2}-\d{2}T/)) {
    data = data.replace('T', ' ').substring(0, 10);
  }
  return data;
}

export function datetimeOrNull(data) {
  if (!data || data === '') return null;
  data = String(data).trim();
  if (data === '') return null;
  if (data.match(/^\d{4}-\d{2}-\d{2}T/)) {
    data = data.replace('T', ' ').substring(0, 19);
  }
  return data;
}

export function numberOrNull(data) {
  if (!data || data === '') return null;
  data = String(data).trim();
  if (data === '') return null;
  try {
    return Number(data);
  } catch (e) {
    return null;
  }
}

export function jsonOrNull(data) {
  if (!data) return null;
  try {
    return JSON.stringify(data);
  } catch (error) {
    return null;
  }
}


export function getTranslationByLang(data, lang_code) {
  let _data = [];
  for (const ent of data ?? []) {
    for (const trans of ent.translations) {
      if (trans.code === lang_code) _data.push({ id: trans.entity_id, name: trans.entity_name });
    }
  }
  return _data;
}


export function loadQueryString(search) {
  return Object.fromEntries(search.replace('?', '').split('&').map(v => v.split('=')).filter(([key, value]) => key));
}

export function createQueryString(queryObject = {}) {
  let queryString = Object.keys(queryObject)
    .filter((key) => queryObject[key] && !(Array.isArray(queryObject[key]) && !queryObject[key].length))
    .map((key) => {
      return Array.isArray(queryObject[key]) ? queryObject[key].map(item => `${(key)}=${(item)}`).join('&') : `${(key)}=${(queryObject[key])}`;
    }).join('&');
  return queryString ? `?${queryString}` : "";
};

export function renderBoolean(val) {
  const isTrue = val === true || val === 'true' || val === 1 || val === '1';
  const isFalse = val === false || val === 'false' || val === 0 || val === '0';

  if (isTrue) return <Typography color="success">true</Typography>;
  if (isFalse) return <Typography sx={{ color: '#d32f2f' }}>false</Typography>;

  if (val == null || val === 'null') return <Typography color="text.secondary">---</Typography>;

  return val;
}

export function prettyJSON(str) {
  try {
    const obj = JSON.parse(str);
    return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(obj, null, 2)}</pre>;
  } catch {
    return str;
  }
};

export function parseDeviceInfo(ua) {
  if (!ua) return { type: 'Unknown', os: 'Unknown' };
  const l = ua.toLowerCase();

  let type = 'Unknown';
  if (l.includes("mobile")) type = "Mobile";
  else if (l.includes("tablet")) type = "Tablet";
  else if (l.includes("windows") || l.includes("macintosh") || l.includes("linux")) type = "Desktop";

  let os = 'Unknown';
  if (l.includes("windows nt 10")) os = "Windows 10";
  else if (l.includes("windows nt 6.1")) os = "Windows 7";
  else if (l.includes("mac os x")) os = "macOS";
  else if (l.includes("android")) os = "Android";
  else if (l.includes("iphone") || l.includes("ipad")) os = "iOS";
  else if (l.includes("ubuntu")) os = "Ubuntu";
  else if (l.includes("debian")) os = "Debian";
  else if (l.includes("fedora")) os = "Fedora";
  else if (l.includes("arch")) os = "Arch Linux";
  else if (l.includes("linux")) os = "Linux";

  return { type, os };
}

export function singularize(plural) {
  if (!plural) return plural;
  if (plural.endsWith('ies')) return plural.slice(0, -3) + 'y';
  if (plural.endsWith('ses')) return plural.slice(0, -2); // e.g. "statuses" → "status"
  if (plural.endsWith('s')) return plural.slice(0, -1);
  return plural;
};